// ========== ADMIN MESSAGING SYSTEM ==========
// Help request with message + Inbox for admin-superadmin communication
import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Modal, 
  Input, 
  List, 
  Badge, 
  Space, 
  Typography, 
  Empty,
  Tag,
  notification,
  Select,
  Checkbox
} from 'antd';
import { 
  InboxOutlined, 
  SendOutlined,
  QuestionCircleOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { database, ref, push, set, onValue, get } from '../../pages/Auth/db/firebase';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

const AdminMessaging = ({ facilityId, userData }) => {
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [inboxModalOpen, setInboxModalOpen] = useState(false);
  const [helpMessage, setHelpMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [cashiers, setCashiers] = useState([]);
  const [cashierMessageModal, setCashierMessageModal] = useState(false);
  const [cashierMessage, setCashierMessage] = useState('');
  const [selectedCashiers, setSelectedCashiers] = useState([]);
  const [sendToAllCashiers, setSendToAllCashiers] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  // Helper function to get display name for a message
  const getDisplayName = (msg) => {
    if (msg.from === 'admin') return 'You';
    
    // If fromName is available, use it
    if (msg.fromName) return msg.fromName;
    
    // If it's a cashier message, try to find the cashier's name
    if (msg.from === 'cashier' && msg.fromId) {
      const cashier = cashiers.find(c => c.id === msg.fromId);
      if (cashier) return cashier.name;
    }
    
    // Fallback to capitalized role
    return msg.from.charAt(0).toUpperCase() + msg.from.slice(1);
  };

  // ========== LISTEN TO INBOX MESSAGES ==========
  useEffect(() => {
    if (!facilityId) return;

    const messagesRef = ref(database, `messages/${facilityId}`);
    
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const messagesList = Object.entries(data).map(([id, msg]) => ({
          id,
          ...msg
        })).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        setMessages(messagesList);
        
        // Count unread messages sent to admin
        const unread = messagesList.filter(m => m.status === 'unread' && m.to === 'admin').length;
        setUnreadCount(unread);
      } else {
        setMessages([]);
        setUnreadCount(0);
      }
    });

    return () => unsubscribe();
  }, [facilityId]);

  // ========== LOAD CASHIERS ==========
  useEffect(() => {
    if (!facilityId) return;

    const loadCashiers = async () => {
      try {
        // Load from users (Firebase auth cashiers)
        const usersSnapshot = await get(ref(database, `users/${facilityId}`));
        const cashiersList = [];
        
        if (usersSnapshot.exists()) {
          const users = usersSnapshot.val();
          Object.entries(users).forEach(([uid, user]) => {
            if (user.role === 'cashier') {
              cashiersList.push({
                id: uid,
                name: user.username || user.name || user.email,
                email: user.email,
                type: 'firebase'
              });
            }
          });
        }

        // Load from cashiers (local cashiers)
        const localSnapshot = await get(ref(database, `cashiers/${facilityId}`));
        if (localSnapshot.exists()) {
          const localCashiers = localSnapshot.val();
          Object.entries(localCashiers).forEach(([id, cashier]) => {
            cashiersList.push({
              id,
              name: cashier.name,
              email: cashier.email,
              type: 'local'
            });
          });
        }

        setCashiers(cashiersList);
      } catch (error) {
        console.error('Error loading cashiers:', error);
      }
    };

    loadCashiers();
  }, [facilityId]);

  // ========== SEND MESSAGE TO CASHIERS ==========
  const handleSendToCashiers = async () => {
    if (!cashierMessage.trim()) {
      api.warning({ message: 'Empty Message', description: 'Please enter a message.' });
      return;
    }

    if (!sendToAllCashiers && selectedCashiers.length === 0) {
      api.warning({ message: 'No Recipients', description: 'Please select at least one cashier or check "Send to all".' });
      return;
    }

    setSending(true);
    try {
      const messagesRef = ref(database, `messages/${facilityId}`);
      const recipients = sendToAllCashiers ? cashiers : cashiers.filter(c => selectedCashiers.includes(c.id));

      for (const cashier of recipients) {
        const newMessageRef = push(messagesRef);
        await set(newMessageRef, {
          from: 'admin',
          fromName: userData?.username || userData?.name || 'Admin',
          to: 'cashier',
          toId: cashier.id,
          toName: cashier.name,
          message: cashierMessage,
          timestamp: new Date().toISOString(),
          status: 'unread',
          facilityId
        });
      }

      api.success({ 
        message: 'Message Sent', 
        description: `Message sent to ${recipients.length} cashier(s).` 
      });
      
      setCashierMessage('');
      setSelectedCashiers([]);
      setSendToAllCashiers(false);
      setCashierMessageModal(false);
    } catch (error) {
      console.error('Error sending message to cashiers:', error);
      api.error({ 
        message: 'Send Failed', 
        description: error.message 
      });
    } finally {
      setSending(false);
    }
  };

  // ========== SEND HELP REQUEST ==========
  const handleSendHelp = async () => {
    if (!helpMessage.trim()) {
      api.warning({ message: 'Empty Message', description: 'Please enter a message.' });
      return;
    }

    setSending(true);
    try {
      // Send to messages for two-way communication
      const messagesRef = ref(database, `messages/${facilityId}`);
      const newMessageRef = push(messagesRef);
      
      await set(newMessageRef, {
        from: 'admin',
        to: 'superadmin',
        message: helpMessage,
        timestamp: new Date().toISOString(),
        status: 'unread',
        facilityId,
        facilityName: facilityId,
        senderName: userData?.name || 'Admin'
      });

      // Also send to help_requests for backwards compatibility
      const helpRef = ref(database, `notifications/help_requests/${new Date().toISOString().split('T')[0]}`);
      const newHelpRef = push(helpRef);
      
      await set(newHelpRef, {
        message: helpMessage,
        facilityId,
        timestamp: new Date().toISOString(),
        status: 'unread',
        type: 'help_request'
      });

      api.success({ 
        message: 'Help Request Sent', 
        description: 'Your message has been sent to support.' 
      });
      
      setHelpMessage('');
      setHelpModalOpen(false);
    } catch (error) {
      console.error('Error sending help request:', error);
      api.error({ 
        message: 'Send Failed', 
        description: error.message 
      });
    } finally {
      setSending(false);
    }
  };

  // ========== MARK MESSAGE AS READ ==========
  const markAsRead = async (messageId) => {
    try {
      const messageRef = ref(database, `messages/${facilityId}/${messageId}`);
      const snapshot = await get(messageRef);
      
      if (snapshot.exists()) {
        const msgData = snapshot.val();
        await set(messageRef, {
          ...msgData,
          status: 'read'
        });
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // ========== REPLY TO MESSAGE ==========
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);

  const handleReply = async () => {
    if (!replyText.trim()) return;

    setSending(true);
    try {
      const messagesRef = ref(database, `messages/${facilityId}`);
      const newMessageRef = push(messagesRef);
      
      await set(newMessageRef, {
        from: 'admin',
        to: 'superadmin',
        message: replyText,
        timestamp: new Date().toISOString(),
        status: 'unread',
        facilityId,
        inReplyTo: replyingTo?.id
      });

      api.success({ message: 'Reply Sent' });
      setReplyText('');
      setReplyModalOpen(false);
      setReplyingTo(null);
    } catch (error) {
      console.error('Error sending reply:', error);
      api.error({ message: 'Send Failed', description: error.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {contextHolder}
      
      {/* Help and Inbox Buttons */}
      <Space>
        <Button
          icon={<QuestionCircleOutlined />}
          onClick={() => setHelpModalOpen(true)}
        >
          Request Help
        </Button>
        
        <Button
          icon={<TeamOutlined />}
          onClick={() => setCashierMessageModal(true)}
          disabled={cashiers.length === 0}
        >
          Message Cashiers
        </Button>
        
        <Badge count={unreadCount} offset={[-5, 5]}>
          <Button
            icon={<InboxOutlined />}
            onClick={() => setInboxModalOpen(true)}
          >
            Inbox
          </Button>
        </Badge>
      </Space>

      {/* Help Request Modal */}
      <Modal
        title={<Space><QuestionCircleOutlined /> Request Help</Space>}
        open={helpModalOpen}
        onCancel={() => setHelpModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setHelpModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            onClick={handleSendHelp}
          >
            Send Request
          </Button>
        ]}
      >
        <TextArea
          rows={6}
          placeholder="Describe your issue or question..."
          value={helpMessage}
          onChange={(e) => setHelpMessage(e.target.value)}
          maxLength={500}
          showCount
        />
      </Modal>

      {/* Inbox Modal */}
      <Modal
        title={<Space><InboxOutlined /> Messages</Space>}
        open={inboxModalOpen}
        onCancel={() => setInboxModalOpen(false)}
        footer={null}
        width={700}
      >
        {messages.length === 0 ? (
          <Empty description="No messages" />
        ) : (
          <List
            dataSource={messages}
            renderItem={(msg) => (
              <List.Item
                key={msg.id}
                onClick={() => msg.status === 'unread' && msg.to === 'admin' && markAsRead(msg.id)}
                style={{
                  cursor: 'pointer',
                  background: msg.status === 'unread' && msg.to === 'admin' ? 'var(--bg-tertiary)' : 'transparent',
                  padding: '12px',
                  borderRadius: '8px'
                }}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Tag color={msg.from === 'admin' ? 'blue' : msg.from === 'cashier' ? 'orange' : 'green'}>
                        {getDisplayName(msg)}
                      </Tag>
                      {msg.status === 'unread' && msg.to === 'admin' && (
                        <Badge status="processing" text="New" />
                      )}
                    </Space>
                  }
                  description={
                    <div style={{ marginTop: '8px' }}>
                      {/* Show replied-to message context if this is a reply */}
                      {msg.inReplyTo && (
                        <div style={{ 
                          background: 'var(--bg-tertiary)', 
                          padding: '8px', 
                          borderRadius: '4px',
                          borderLeft: '3px solid var(--primary-color)',
                          marginBottom: '8px'
                        }}>
                          <Text type="secondary" style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                            Replying to:
                          </Text>
                          <div style={{ marginTop: '4px' }}>
                            <Text style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                              {messages.find(m => m.id === msg.inReplyTo)?.message || 'Original message not found'}
                            </Text>
                          </div>
                        </div>
                      )}
                      <Text style={{ color: 'var(--lcolor)' }}>{msg.message}</Text>
                      <div style={{ marginTop: '8px' }}>
                        <Text type="secondary" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {new Date(msg.timestamp).toLocaleString()}
                        </Text>
                      </div>
                      {msg.from === 'superadmin' && (
                        <Button
                          size="small"
                          type="link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReplyingTo(msg);
                            setReplyModalOpen(true);
                          }}
                        >
                          Reply
                        </Button>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* Reply Modal */}
      <Modal
        title="Reply to Message"
        open={replyModalOpen}
        onCancel={() => {
          setReplyModalOpen(false);
          setReplyingTo(null);
          setReplyText('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setReplyModalOpen(false);
            setReplyingTo(null);
            setReplyText('');
          }}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            onClick={handleReply}
          >
            Send Reply
          </Button>
        ]}
      >
        {replyingTo && (
          <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>Replying to:</Text>
            <div><Text >{replyingTo.message}</Text></div>
          </div>
        )}
        <TextArea
          rows={4}
          placeholder="Type your reply..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          maxLength={500}
          showCount
        />
      </Modal>

      {/* Message Cashiers Modal */}
      <Modal
        title={<Space><TeamOutlined /> Send Message to Cashiers</Space>}
        open={cashierMessageModal}
        onCancel={() => {
          setCashierMessageModal(false);
          setCashierMessage('');
          setSelectedCashiers([]);
          setSendToAllCashiers(false);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setCashierMessageModal(false);
            setCashierMessage('');
            setSelectedCashiers([]);
            setSendToAllCashiers(false);
          }}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            onClick={handleSendToCashiers}
          >
            Send Message
          </Button>
        ]}
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Checkbox
              checked={sendToAllCashiers}
              onChange={(e) => {
                setSendToAllCashiers(e.target.checked);
                if (e.target.checked) {
                  setSelectedCashiers([]);
                }
              }}
            >
              Send to all cashiers ({cashiers.length})
            </Checkbox>
          </div>
          
          {!sendToAllCashiers && (
            <div>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>Select Recipients:</Text>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Choose one or more cashiers"
                value={selectedCashiers}
                onChange={setSelectedCashiers}
                disabled={sendToAllCashiers}
              >
                {cashiers.map(cashier => (
                  <Option key={cashier.id} value={cashier.id}>
                    {cashier.name} {cashier.email && `(${cashier.email})`}
                  </Option>
                ))}
              </Select>
            </div>
          )}
          
          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>Message:</Text>
            <TextArea
              rows={6}
              placeholder="Type your message to cashiers..."
              value={cashierMessage}
              onChange={(e) => setCashierMessage(e.target.value)}
              maxLength={500}
              showCount
            />
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default AdminMessaging;
// ========== ADMIN MESSAGING SYSTEM ENDS ==========
